import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    CpmButton,
    CpmModal,
    CpmModalBody,
    CpmModalFooter,
    CpmModalHeader,
    CpmStatusChip,
} from '../../../components/design-system';
import {
    CPM_TENANT_MERGE_ACTION,
    formatCpmCount,
    formatFlaggedDate,
    formatVolumeLabel,
} from '../../../constants/centralProductMaster.constants';
import { getMasterDetails, mergeMasters } from '../../../services/productMasterService';
import type {
    CpmMasterDetailsResponse,
    CpmTenantMergeAction,
} from '../../../types/centralProductMaster.types';
import { getApiErrorMessage } from '../../../utils/api/ApiClient';
import BarcodeOverlap from './BarcodeOverlap';
import BarcodeThirdPartyWarning, {
    unionBarcodeThirdPartyClaims,
} from './BarcodeThirdPartyWarning';
import BrandProductCollisions, {
    brandCollisionGaps,
    brandMovingProductCount,
    brandProductCollisionsResolved,
    buildBrandProductChoicesPayload,
    findBrandProductCollisionPairs,
} from './BrandProductCollisions';
import ProductVolumeCollisions, {
    buildProductVolumeChoicesPayload,
    productCollisionsResolved,
} from './ProductVolumeCollisions';
import VariantStateCollisions, {
    buildVariantStateChoicesPayload,
    variantCollisionsResolved,
} from './VariantStateCollisions';

interface MergeDecisionProps {
    open: boolean;
    level: 'brand' | 'product' | 'variant';
    idA: string;
    idB: string;
    /** Breadcrumb parent label — design default “Verification Queue”. */
    backLabel?: string;
    onClose: () => void;
    onMerged: () => void;
    /** Opens the survivor record after a successful merge (Master Editor form / deep-link). */
    onOpenSurvivor?: (level: 'brand' | 'product' | 'variant', survivorId: string) => void;
}

type Phase = 'choose' | 'working' | 'done' | 'error';
type LoadPhase = 'loading' | 'ready' | 'error';

function truncLabel(name: string, max = 34): string {
    if (name.length <= max) return name;
    return `${name.slice(0, max - 2)}…`;
}

function originRow(details: CpmMasterDetailsResponse | null): { label: string; value: string } {
    const status = String(details?.record?.status ?? '');
    const provenance = details?.record?.provenance as { createdAt?: unknown } | undefined;
    const raw =
        provenance?.createdAt != null
            ? String(provenance.createdAt)
            : details?.record?.createdAt != null
              ? String(details.record.createdAt)
              : null;
    return {
        label: status === 'ACTIVE' ? 'Live since' : 'Created',
        value: formatFlaggedDate(raw),
    };
}

function brandNameOf(details: CpmMasterDetailsResponse | null): string {
    return String((details?.brand as { name?: string } | undefined)?.name ?? '—');
}

function asStringList(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value.map((v) => String(v ?? '').trim()).filter(Boolean);
}

function uniqStrings(values: string[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const v of values) {
        const key = v.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(v);
    }
    return out;
}

function aliasesOf(details: CpmMasterDetailsResponse | null): string[] {
    return asStringList(details?.record?.aliases);
}

function gtinsOf(details: CpmMasterDetailsResponse | null): string[] {
    return asStringList(details?.record?.gtin);
}

function nameOf(details: CpmMasterDetailsResponse | null, fallbackId: string) {
    return String(details?.record?.name ?? fallbackId);
}

function recordStr(details: CpmMasterDetailsResponse | null, key: string): string {
    const v = details?.record?.[key];
    if (v == null || v === '') return '';
    return String(v);
}

function levelBadge(level: MergeDecisionProps['level']) {
    if (level === 'brand') return 'Brand-level record';
    if (level === 'product') return 'Product-level record';
    return 'Variant-level record';
}

function levelUpper(level: MergeDecisionProps['level']) {
    if (level === 'brand') return 'BRAND';
    if (level === 'product') return 'PRODUCT';
    return 'VARIANT';
}

function MergeIcon({ size = 13 }: { size?: number }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M7 3v4a1 1 0 0 0 1 1h4" />
            <path d="M17 21v-4a1 1 0 0 0-1-1h-4" />
            <path d="M3 7h4" />
            <path d="m21 17-4-4-4 4" />
            <path d="M12 8 8 12l4 4" />
        </svg>
    );
}

export default function MergeDecision({
    open,
    level,
    idA,
    idB,
    backLabel = 'Verification Queue',
    onClose,
    onMerged,
    onOpenSurvivor,
}: MergeDecisionProps) {
    const [survivorId, setSurvivorId] = useState<string | null>(null);
    const [sideA, setSideA] = useState<CpmMasterDetailsResponse | null>(null);
    const [sideB, setSideB] = useState<CpmMasterDetailsResponse | null>(null);
    const [tenantAction, setTenantAction] = useState<CpmTenantMergeAction | null>(null);
    const [ack, setAck] = useState(false);
    const [loadPhase, setLoadPhase] = useState<LoadPhase>('loading');
    const [phase, setPhase] = useState<Phase>('choose');
    const [mergeError, setMergeError] = useState('');
    const [openChildren, setOpenChildren] = useState<Record<string, boolean>>({});
    const [volumeChoices, setVolumeChoices] = useState<Record<string, string>>({});
    const [volStateChoices, setVolStateChoices] = useState<Record<string, Record<string, string>>>(
        {}
    );
    const [openVolStates, setOpenVolStates] = useState<Record<string, boolean>>({});
    /** Variant-level: state → winning side id (idA / idB). */
    const [stateChoices, setStateChoices] = useState<Record<string, string>>({});
    const [productChoices, setProductChoices] = useState<Record<string, string>>({});
    const [brandVolumeChoices, setBrandVolumeChoices] = useState<
        Record<string, Record<string, string>>
    >({});
    const [brandVolStateChoices, setBrandVolStateChoices] = useState<
        Record<string, Record<string, Record<string, string>>>
    >({});
    const [openProdVol, setOpenProdVol] = useState<Record<string, boolean>>({});
    const [openBrandVolStates, setOpenBrandVolStates] = useState<
        Record<string, Record<string, boolean>>
    >({});

    const loadBoth = async () => {
        setLoadPhase('loading');
        setSideA(null);
        setSideB(null);
        try {
            const [a, b] = await Promise.all([
                getMasterDetails({ level, id: idA, includeChildren: true, otherId: idB }),
                getMasterDetails({ level, id: idB, includeChildren: true, otherId: idA }),
            ]);
            setSideA(a);
            setSideB(b);
            setLoadPhase('ready');
        } catch {
            setLoadPhase('error');
        }
    };

    useEffect(() => {
        if (!open) return;
        setSurvivorId(null);
        setTenantAction(null);
        setAck(false);
        setPhase('choose');
        setMergeError('');
        setOpenChildren({});
        setVolumeChoices({});
        setVolStateChoices({});
        setOpenVolStates({});
        setStateChoices({});
        setProductChoices({});
        setBrandVolumeChoices({});
        setBrandVolStateChoices({});
        setOpenProdVol({});
        setOpenBrandVolStates({});
        void loadBoth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, level, idA, idB]);

    const loading = loadPhase === 'loading';

    const leftoverClaims = useMemo(
        () => unionBarcodeThirdPartyClaims(sideA, sideB),
        [sideA, sideB]
    );

    const loserId = survivorId == null ? null : survivorId === idA ? idB : idA;
    const survivor = survivorId == null ? null : survivorId === idA ? sideA : sideB;
    const loser = loserId == null ? null : loserId === idA ? sideA : sideB;

    const brandPairs = useMemo(
        () => (level === 'brand' ? findBrandProductCollisionPairs(sideA, sideB) : []),
        [level, sideA, sideB]
    );

    const unresolvedVariantStates = useMemo(() => {
        if (level !== 'variant' || !sideA || !sideB) return [] as string[];
        const aStates = new Set(
            (sideA.stateProducts ?? []).map((sp) => String(sp.state ?? '')).filter(Boolean)
        );
        const colliding: string[] = [];
        for (const sp of sideB.stateProducts ?? []) {
            const state = String(sp.state ?? '');
            if (state && aStates.has(state) && !colliding.includes(state)) colliding.push(state);
        }
        return colliding.filter((s) => !stateChoices[s]);
    }, [level, sideA, sideB, stateChoices]);

    const collisionsResolved =
        (level !== 'product' ||
            productCollisionsResolved(sideA, sideB, volumeChoices, volStateChoices)) &&
        (level !== 'variant' || variantCollisionsResolved(sideA, sideB, stateChoices)) &&
        (level !== 'brand' ||
            brandProductCollisionsResolved(
                brandPairs,
                productChoices,
                brandVolumeChoices,
                brandVolStateChoices
            ));

    const retiredTenantCount = loser?.tenantLinkCount ?? 0;
    const tenantActionNeeded = retiredTenantCount > 0;
    const tenantReady = !tenantActionNeeded || !!tenantAction;

    const canConfirm =
        !!survivorId &&
        !!loserId &&
        ack &&
        tenantReady &&
        collisionsResolved &&
        !loading &&
        phase === 'choose';

    const aliasLabel = level === 'brand' ? 'Known aliases' : 'Known alternate names';
    const childLabel =
        level === 'brand'
            ? 'Products under this brand'
            : level === 'product'
              ? 'Variants under this product'
              : '';

    const impact = useMemo(() => {
        if (!survivor || !loser || !survivorId || !loserId) return null;
        const survivorName = nameOf(survivor, survivorId);
        const retiredName = nameOf(loser, loserId);
        const survivorAliases = aliasesOf(survivor);
        const retiredAliases = aliasesOf(loser);
        const aliasUnion = uniqStrings([...survivorAliases, ...retiredAliases, retiredName]);
        const survivorGtins = gtinsOf(survivor);
        const retiredGtins = gtinsOf(loser);
        const barcodeUnion = uniqStrings([...survivorGtins, ...retiredGtins]);
        const tenantCount = loser.tenantLinkCount ?? 0;
        const collidingPairCount = level === 'brand' ? brandPairs.length : 0;
        const movingOnlyCount =
            level === 'brand' ? brandMovingProductCount(loser, brandPairs) : 0;
        const childCount =
            level === 'brand'
                ? collidingPairCount > 0
                    ? movingOnlyCount
                    : (loser.affectedProducts?.length ?? 0)
                : level === 'product'
                  ? (loser.affectedVariants?.length ?? 0)
                  : 0;

        const tenantTile = {
            value: formatCpmCount(tenantCount),
            label:
                tenantCount === 0
                    ? 'Tenant records affected'
                    : tenantAction === CPM_TENANT_MERGE_ACTION.REPOINT
                      ? 'Store brands re-pointed'
                      : tenantAction === CPM_TENANT_MERGE_ACTION.UNLINK
                        ? 'Store brands unlinked'
                        : 'Store brands awaiting a decision',
            sub:
                tenantCount === 0
                    ? `Nothing tenant-facing points at ${retiredName}.`
                    : tenantAction === CPM_TENANT_MERGE_ACTION.REPOINT
                      ? `They will point at ${survivorName} instead.`
                      : tenantAction === CPM_TENANT_MERGE_ACTION.UNLINK
                        ? 'Their master pointer is cleared; the next daily linking run re-evaluates them.'
                        : 'Pick re-point or unlink below before you can merge.',
        };

        const tiles =
            level === 'brand'
                ? collidingPairCount > 0
                    ? [
                          {
                              value: formatCpmCount(aliasUnion.length),
                              label: 'Aliases kept',
                              sub: 'Both brands’ known aliases, plus the retired brand name itself, become aliases on the survivor.',
                          },
                          {
                              value: formatCpmCount(movingOnlyCount),
                              label: 'Products moved',
                              sub:
                                  movingOnlyCount === 0
                                      ? `No product is filed only under ${retiredName}.`
                                      : `Products filed only under ${retiredName} move to ${survivorName} unchanged — no choice needed.`,
                          },
                          {
                              value: formatCpmCount(collidingPairCount),
                              label: 'Same-name products',
                              sub: `Both brands carry ${collidingPairCount === 1 ? 'this name' : 'these names'}. For each, the product you pick stays; the other is retired into it — never stacked as a duplicate.`,
                          },
                          tenantTile,
                      ]
                    : [
                          {
                              value: formatCpmCount(aliasUnion.length),
                              label: 'Aliases kept',
                              sub: 'Both brands’ known aliases, plus the retired brand name itself, become aliases on the survivor.',
                          },
                          {
                              value: formatCpmCount(childCount),
                              label: 'Products re-parented',
                              sub:
                                  childCount === 0
                                      ? 'The retired brand has no catalog products under it.'
                                      : `Products currently filed under ${retiredName} move to ${survivorName}.`,
                          },
                          tenantTile,
                      ]
                : level === 'product'
                  ? [
                        {
                            value: formatCpmCount(aliasUnion.length),
                            label: 'Alternate names kept',
                            sub: 'Both products’ known names, plus the retired title itself, become aliases on the survivor.',
                        },
                        {
                            value: formatCpmCount(childCount),
                            label: 'Variants re-parented',
                            sub:
                                childCount === 0
                                    ? 'The retired product has no variants under it.'
                                    : `Variants filed under ${retiredName} move to ${survivorName} — non-colliding packs are re-parented as they are.`,
                        },
                        tenantTile,
                    ]
                  : [
                        {
                            value: formatCpmCount(barcodeUnion.length),
                            label: 'Barcodes / GTINs kept',
                            sub: `Union of both records — ${survivorGtins.length} from the survivor + ${retiredGtins.length} from the retired record.`,
                        },
                        {
                            value: formatCpmCount(aliasUnion.length),
                            label: 'Alternate names kept',
                            sub: 'Both records’ known names, plus the retired title itself, become aliases on the survivor.',
                        },
                        tenantTile,
                    ];

        const absorbTitle =
            level === 'brand'
                ? 'The survivor absorbs both brands’ names'
                : level === 'product'
                  ? 'The survivor absorbs both products’ names and variants'
                  : 'The survivor absorbs both records’ identifiers';
        const absorbBody =
            level === 'brand'
                ? collidingPairCount > 0
                    ? `${survivorName} keeps the combined aliases above. ${formatCpmCount(movingOnlyCount)} product${movingOnlyCount === 1 ? '' : 's'} filed only under ${retiredName} move to it. For each same-name product, the one you pick stays and the other is retired into it — not added as a second product with the same name.`
                    : `${survivorName} keeps the combined aliases above, and every product filed under ${retiredName} is re-parented to it — nothing is dropped.`
                : level === 'product'
                  ? `${survivorName} keeps the combined alternate names above, and the variants under ${retiredName} are re-parented to it — nothing is dropped.`
                  : `${survivorName} ends up with the combined barcodes and alternate names above — nothing on ${retiredName} is dropped.`;

        const tenantRowTitle =
            tenantCount === 0
                ? 'No tenant records to act on'
                : tenantAction === CPM_TENANT_MERGE_ACTION.REPOINT
                  ? `${formatCpmCount(tenantCount)} tenant-facing record${tenantCount === 1 ? ' re-points' : 's re-point'} to the survivor`
                  : tenantAction === CPM_TENANT_MERGE_ACTION.UNLINK
                    ? `${formatCpmCount(tenantCount)} tenant-facing record${tenantCount === 1 ? ' is' : 's are'} unlinked`
                    : 'Tenant documents still need a decision';
        const tenantRowBody =
            (tenantCount === 0
                ? `Nothing tenant-facing currently references ${retiredName}, so there is nothing to re-point or unlink.`
                : tenantAction === CPM_TENANT_MERGE_ACTION.REPOINT
                  ? `Store brands that point at ${retiredName} will point at ${survivorName} instead.`
                  : tenantAction === CPM_TENANT_MERGE_ACTION.UNLINK
                    ? `Their pointer at ${retiredName} is cleared, leaving them unlinked until the next daily linking run re-evaluates them.`
                    : 'Choose re-point or unlink in the block above.') +
            ' Tenant records update within moments of confirming — a follow-up step, not an overnight job.';

        const conflicts: Array<{ field: string; retired: string; survivor: string }> = [];
        if (level === 'brand') {
            const sc = recordStr(survivor, 'countryOfOrigin') || '—';
            const rc = recordStr(loser, 'countryOfOrigin') || '—';
            if (sc !== rc) conflicts.push({ field: 'Country', retired: rc, survivor: sc });
            const sp = recordStr(survivor, 'parentCompany') || '—';
            const rp = recordStr(loser, 'parentCompany') || '—';
            if (sp !== rp) conflicts.push({ field: 'Parent company', retired: rp, survivor: sp });
        } else if (level === 'product') {
            const sb = brandNameOf(survivor);
            const rb = brandNameOf(loser);
            if (sb !== rb) conflicts.push({ field: 'Brand', retired: rb, survivor: sb });
            const sabv = survivor.record?.alcoholContent;
            const rabv = loser.record?.alcoholContent;
            const sa = sabv == null || sabv === '' ? '—' : `${sabv}%`;
            const ra = rabv == null || rabv === '' ? '—' : `${rabv}%`;
            if (sa !== ra) conflicts.push({ field: 'Alcohol content', retired: ra, survivor: sa });
        } else {
            const sb = brandNameOf(survivor);
            const rb = brandNameOf(loser);
            if (sb !== rb) conflicts.push({ field: 'Brand', retired: rb, survivor: sb });
            const svRaw = formatVolumeLabel(
                survivor.record?.volumeMl as number | undefined,
                survivor.record?.volumeUnit as string | undefined
            );
            const rvRaw = formatVolumeLabel(
                loser.record?.volumeMl as number | undefined,
                loser.record?.volumeUnit as string | undefined
            );
            const sVol = !svRaw || svRaw === '—' ? 'Not recorded' : svRaw;
            const rVol = !rvRaw || rvRaw === '—' ? 'Not recorded' : rvRaw;
            if (sVol !== rVol) conflicts.push({ field: 'Volume', retired: rVol, survivor: sVol });
            const sip =
                survivor.record?.itemPerCase != null
                    ? String(survivor.record.itemPerCase)
                    : 'Not recorded';
            const rip =
                loser.record?.itemPerCase != null
                    ? String(loser.record.itemPerCase)
                    : 'Not recorded';
            if (sip !== rip) conflicts.push({ field: 'Items / case', retired: rip, survivor: sip });
        }

        const aliasNoun = level === 'brand' ? 'aliases' : 'alternate names';
        const aliasPreview = aliasUnion.map((text) => {
            const isNew = !survivorAliases.some((a) => a.toLowerCase() === text.toLowerCase());
            const isRetiredName = text.toLowerCase() === retiredName.toLowerCase();
            return {
                text,
                isNew,
                tag: isRetiredName ? 'RETIRING NAME' : 'ADDED',
            };
        });
        const addedCount = aliasPreview.filter((a) => a.isNew).length;
        const aliasPreviewNote =
            addedCount === 0
                ? `Every ${level === 'brand' ? 'alias' : 'alternate name'} on ${retiredName} is already recorded on ${survivorName} — nothing new is written.`
                : `${formatCpmCount(addedCount)} new ${addedCount === 1 ? 'entry is' : 'entries are'} added, including the retiring name “${retiredName}”. Existing entries are unchanged.`;

        const tNoun = tenantCount === 1 ? 'store-brand record' : 'store-brand records';
        const tenantEffect =
            tenantCount === 0
                ? `No store-brand record points at ${retiredName}, so nothing tenant-facing changes.`
                : tenantAction === CPM_TENANT_MERGE_ACTION.REPOINT
                  ? `${formatCpmCount(tenantCount)} ${tNoun} will be re-pointed to ${survivorName}.`
                  : tenantAction === CPM_TENANT_MERGE_ACTION.UNLINK
                    ? `${formatCpmCount(tenantCount)} ${tNoun} will be unlinked and re-evaluated by the next daily linking run.`
                    : `${formatCpmCount(tenantCount)} ${tNoun} point at ${retiredName} — choose what happens to them.`;

        return {
            tiles,
            absorbTitle,
            absorbBody,
            retiredName,
            survivorName,
            tenantCount,
            tenantRowTitle,
            tenantRowBody,
            conflicts,
            aliasUnion,
            aliasPreview,
            aliasPreviewTitle: `${survivorName} — ${aliasNoun} after merge`,
            aliasPreviewNote,
            tenantEffect,
            collidingPairCount,
            movingOnlyCount,
            survivorAliases,
        };
    }, [survivor, loser, survivorId, loserId, level, tenantAction, brandPairs]);

    const confirmLabel = (() => {
        if (!survivorId || !impact) return 'Merge and retire';
        if (tenantActionNeeded && !tenantAction) return 'Merge — choose a tenant action first';
        if (!tenantActionNeeded || impact.tenantCount === 0) {
            return `Merge — retire ${truncLabel(impact.retiredName)}`;
        }
        const tNoun = impact.tenantCount === 1 ? 'store-brand record' : 'store-brand records';
        if (tenantAction === CPM_TENANT_MERGE_ACTION.REPOINT) {
            return `Merge — re-point ${formatCpmCount(impact.tenantCount)} ${tNoun}`;
        }
        if (tenantAction === CPM_TENANT_MERGE_ACTION.UNLINK) {
            return `Merge — unlink ${formatCpmCount(impact.tenantCount)} ${tNoun}`;
        }
        return 'Merge and retire';
    })();

    const verdict = (() => {
        if (!impact) {
            return {
                label: 'No survivor chosen yet',
                hint: 'Pick the record that stays before you can merge.',
                tone: 'navy' as const,
            };
        }
        if (level === 'variant' && unresolvedVariantStates.length > 0) {
            return {
                label: `${unresolvedVariantStates.length} state pricing collision${unresolvedVariantStates.length === 1 ? '' : 's'} unresolved`,
                hint: `Pick the surviving pricing record for ${unresolvedVariantStates.join(', ')} before you can merge.`,
                tone: 'navy' as const,
            };
        }
        if (
            level === 'product' &&
            !productCollisionsResolved(sideA, sideB, volumeChoices, volStateChoices)
        ) {
            return {
                label: 'Same-volume variant collisions unresolved',
                hint: 'Pick the surviving variant (and any nested state pricing) for every colliding volume before you can merge.',
                tone: 'navy' as const,
            };
        }
        if (level === 'brand' && brandPairs.length > 0) {
            const gaps = brandCollisionGaps(
                brandPairs,
                productChoices,
                brandVolumeChoices,
                brandVolStateChoices
            );
            if (gaps.unresolvedProducts.length > 0) {
                return {
                    label: `${gaps.unresolvedProducts.length} same-name product ${gaps.unresolvedProducts.length === 1 ? 'collision unresolved' : 'collisions unresolved'}`,
                    hint: `Pick which product survives for ${gaps.unresolvedProducts.join(', ')} before you can merge.`,
                    tone: 'navy' as const,
                };
            }
            if (gaps.unresolvedVolumes > 0) {
                return {
                    label: `${gaps.unresolvedVolumes} same-volume variant ${gaps.unresolvedVolumes === 1 ? 'collision unresolved' : 'collisions unresolved'}`,
                    hint: 'Under the same-name products, pick the surviving pack at every overlapping volume before you can merge.',
                    tone: 'navy' as const,
                };
            }
            if (gaps.unresolvedStates > 0) {
                return {
                    label: `${gaps.unresolvedStates} state pricing ${gaps.unresolvedStates === 1 ? 'collision unresolved' : 'collisions unresolved'}`,
                    hint: 'Under those overlapping packs, pick which pricing row survives for every shared state before you can merge.',
                    tone: 'navy' as const,
                };
            }
        }
        // Tenant re-point/unlink stays gated in the body + CTA — footer always uses
        // the design “will survive” verdict once a survivor is chosen.
        return {
            label: `${impact.survivorName} will survive`,
            hint: `${impact.retiredName} will be retired and marked as merged into it. ${impact.tenantEffect}`,
            tone: 'purple' as const,
        };
    })();

    const pickSurvivor = (id: string) => {
        setSurvivorId(id);
        setTenantAction(null);
        setAck(false);
    };

    const confirm = async () => {
        if (!survivorId || !loserId) return;
        const action =
            tenantActionNeeded && tenantAction ? tenantAction : CPM_TENANT_MERGE_ACTION.REPOINT;
        try {
            setPhase('working');
            setMergeError('');
            const body = {
                level,
                survivorId,
                loserId,
                tenantAction: action,
                productChoices:
                    level === 'brand' && brandPairs.length > 0
                        ? buildBrandProductChoicesPayload(
                              brandPairs,
                              productChoices,
                              brandVolumeChoices,
                              brandVolStateChoices
                          )
                        : undefined,
                volumeChoices:
                    level === 'product' && sideA && sideB
                        ? buildProductVolumeChoicesPayload(
                              sideA,
                              sideB,
                              volumeChoices,
                              volStateChoices
                          )
                        : undefined,
                stateChoices:
                    level === 'variant'
                        ? buildVariantStateChoicesPayload(stateChoices, survivorId)
                        : undefined,
            };
            await mergeMasters(body);
            onMerged();
            setPhase('done');
        } catch (err) {
            setMergeError(
                getApiErrorMessage(err) ||
                    'The catalog service did not complete the merge. Both records are unchanged and still live — nothing was retired.'
            );
            setPhase('error');
        }
    };

    const sides = [
        { id: idA, details: sideA },
        { id: idB, details: sideB },
    ];

    type MetaRow = {
        key: string;
        label: string;
        value: string;
        /** Diff-highlightable field (design conflictField). */
        conflictable?: boolean;
        /** Zero-count muted tone. */
        muted?: boolean;
    };

    const metaRowsFor = (details: CpmMasterDetailsResponse | null): MetaRow[] => {
        if (level === 'brand') {
            const products = details?.affectedProducts?.length ?? 0;
            const origin = originRow(details);
            return [
                {
                    key: 'country',
                    label: 'Country of origin',
                    value: recordStr(details, 'countryOfOrigin') || '—',
                    conflictable: true,
                },
                {
                    key: 'parent',
                    label: 'Parent company',
                    value: recordStr(details, 'parentCompany') || 'Not recorded',
                    conflictable: true,
                },
                {
                    key: 'products',
                    label: 'Catalog products',
                    value:
                        products === 0
                            ? 'None yet'
                            : `${formatCpmCount(products)} product${products === 1 ? '' : 's'}`,
                    muted: products === 0,
                },
                { key: 'origin', label: origin.label, value: origin.value },
            ];
        }
        if (level === 'product') {
            const abv = details?.record?.alcoholContent;
            const tenants = details?.tenantLinkCount ?? 0;
            const origin = originRow(details);
            return [
                { key: 'brand', label: 'Brand', value: brandNameOf(details), conflictable: true },
                {
                    key: 'abv',
                    label: 'Alcohol content',
                    value: abv == null || abv === '' ? '—' : `${abv}%`,
                    conflictable: true,
                },
                {
                    key: 'tenants',
                    label: 'Linked store brands',
                    value:
                        tenants === 0
                            ? 'None yet'
                            : `${formatCpmCount(tenants)} store brand${tenants === 1 ? '' : 's'}`,
                    muted: tenants === 0,
                },
                { key: 'origin', label: origin.label, value: origin.value },
            ];
        }
        const tenants = details?.tenantLinkCount ?? 0;
        const volRaw = formatVolumeLabel(
            details?.record?.volumeMl as number | undefined,
            details?.record?.volumeUnit as string | undefined
        );
        const vol = !volRaw || volRaw === '—' ? 'Not recorded' : volRaw;
        const origin = originRow(details);
        return [
            { key: 'brand', label: 'Brand', value: brandNameOf(details), conflictable: true },
            { key: 'volume', label: 'Volume', value: vol, conflictable: true },
            {
                key: 'itemPerCase',
                label: 'Items / case',
                value:
                    details?.record?.itemPerCase != null
                        ? String(details.record.itemPerCase)
                        : 'Not recorded',
                conflictable: true,
            },
            {
                key: 'tenants',
                label: 'Linked store brands',
                value:
                    tenants === 0
                        ? 'None yet'
                        : `${formatCpmCount(tenants)} store brand${tenants === 1 ? '' : 's'}`,
                muted: tenants === 0,
            },
            { key: 'origin', label: origin.label, value: origin.value },
        ];
    };

    const conflictKeys = (() => {
        if (survivorId == null || !sideA || !sideB) return new Set<string>();
        const aRows = metaRowsFor(sideA);
        const bByKey = new Map(metaRowsFor(sideB).map((r) => [r.key, r.value]));
        const keys = new Set<string>();
        for (const row of aRows) {
            if (!row.conflictable) continue;
            if (row.value !== bByKey.get(row.key)) keys.add(row.key);
        }
        return keys;
    })();

    const childrenFor = (details: CpmMasterDetailsResponse | null) => {
        if (level === 'brand') {
            return (details?.affectedProducts ?? []).map((p) => ({
                id: p._id,
                name: p.name,
                status: p.status,
                volume: '',
            }));
        }
        if (level === 'product') {
            return (details?.affectedVariants ?? []).map((v) => ({
                id: v._id,
                name: v.name,
                status: v.status,
                volume: formatVolumeLabel(v.volumeMl, v.volumeUnit),
            }));
        }
        return [];
    };

    return (
        <CpmModal open={open} onClose={onClose} maxWidth={1200} zIndex={76} variant="page">
            <CpmModalHeader className="!px-7 !pt-4 !pb-[18px]">
                <div
                    className="flex items-center text-[var(--cpm-text-secondary)]"
                    style={{ gap: 8, fontSize: 12.5, fontWeight: 600, marginBottom: 14 }}
                >
                    <span>Admin</span>
                    <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                        /
                    </span>
                    <span>Catalog</span>
                    <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                        /
                    </span>
                    <span>Central Product Master</span>
                    <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                        /
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-[var(--cpm-text-secondary)] hover:text-[var(--cpm-brand-navy)]"
                    >
                        {backLabel}
                    </button>
                    <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                        /
                    </span>
                    <span className="text-[var(--cpm-brand-navy)]" style={{ fontWeight: 800 }}>
                        Merge decision
                    </span>
                </div>
                <div className="flex items-start justify-between flex-wrap" style={{ gap: 28 }}>
                    <div className="flex flex-col min-w-0" style={{ gap: 9 }}>
                        <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
                            <span
                                className="inline-flex items-center rounded-full bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)] uppercase"
                                style={{
                                    gap: 7,
                                    padding: '4px 11px',
                                    fontSize: 10.5,
                                    fontWeight: 800,
                                    letterSpacing: '0.07em',
                                }}
                            >
                                <MergeIcon />
                                Merge decision
                            </span>
                            <span
                                className="inline-flex items-center rounded-full bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] text-[var(--cpm-text-secondary)] uppercase"
                                style={{
                                    padding: '4px 11px',
                                    fontSize: 10.5,
                                    fontWeight: 800,
                                    letterSpacing: '0.06em',
                                }}
                            >
                                {levelBadge(level)}
                            </span>
                        </div>
                        <h1 className="m-0 !text-[24px] !font-extrabold !tracking-[-0.025em] !leading-[1.15] !text-[var(--cpm-brand-navy)] text-pretty">
                            Collapse two {level} records into one
                        </h1>
                        <div
                            className="flex items-center flex-wrap text-[var(--cpm-text-primary)]"
                            style={{ gap: 12, fontSize: 13.5, fontWeight: 700 }}
                        >
                            <span className="max-w-[360px] truncate">{nameOf(sideA, idA)}</span>
                            <span
                                className="inline-flex items-center justify-center shrink-0 bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                                style={{ width: 24, height: 24, borderRadius: 7 }}
                            >
                                <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.4"
                                >
                                    <path
                                        d="M8 3 4 7l4 4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path d="M4 7h16" strokeLinecap="round" />
                                    <path
                                        d="m16 21 4-4-4-4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path d="M20 17H4" strokeLinecap="round" />
                                </svg>
                            </span>
                            <span className="max-w-[360px] truncate">{nameOf(sideB, idB)}</span>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center shrink-0 border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-text-secondary)] hover:border-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-navy)]"
                        style={{
                            gap: 7,
                            height: 38,
                            padding: '0 15px',
                            borderRadius: 10,
                            fontSize: 12.5,
                            fontWeight: 800,
                        }}
                    >
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                        >
                            <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                        </svg>
                        Cancel — don&apos;t merge
                    </button>
                </div>
            </CpmModalHeader>

            <CpmModalBody clampScroll={false} className="!gap-[22px]">
                {loadPhase === 'loading' ? (
                    <div className="flex flex-col" style={{ gap: 14, padding: '8px 0 12px' }}>
                        <div className="flex items-center" style={{ gap: 10 }}>
                            <span className="cpm-spin" />
                            <span
                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em' }}
                            >
                                Loading both records
                            </span>
                        </div>
                        <div
                            className="grid"
                            style={{
                                gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)',
                                gap: 16,
                            }}
                        >
                            <span className="cpm-sk" style={{ height: 280, borderRadius: 14 }} />
                            <span className="cpm-sk" style={{ height: 280, borderRadius: 14 }} />
                        </div>
                    </div>
                ) : loadPhase === 'error' ? (
                    <div
                        className="flex flex-col items-center text-center"
                        style={{ gap: 14, padding: '44px 24px 32px' }}
                    >
                        <span
                            className="inline-flex items-center justify-center bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)]"
                            style={{ width: 56, height: 56, borderRadius: 14 }}
                        >
                            <svg
                                width="26"
                                height="26"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                <path d="M12 9v4" />
                                <path d="M12 17h.01" />
                            </svg>
                        </span>
                        <span
                            className="text-[var(--cpm-brand-navy)]"
                            style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}
                        >
                            Couldn&apos;t load both records
                        </span>
                        <span
                            className="text-[var(--cpm-text-secondary)] max-w-[400px]"
                            style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.5 }}
                        >
                            The catalog service did not return both sides of this merge. Nothing was
                            changed — try again.
                        </span>
                        <div className="flex items-center" style={{ gap: 10, marginTop: 4 }}>
                            <CpmButton variant="outline" size="modal" onClick={onClose}>
                                Close
                            </CpmButton>
                            <CpmButton
                                variant="navy"
                                size="modal"
                                onClick={() => void loadBoth()}
                                leadingIcon={
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                                        <path d="M3 3v5h5" />
                                    </svg>
                                }
                            >
                                Try again
                            </CpmButton>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex flex-col" style={{ gap: 12 }}>
                            <div className="flex items-baseline flex-wrap" style={{ gap: 12 }}>
                                <span
                                    className="uppercase text-[var(--cpm-text-secondary)]"
                                    style={{
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.08em',
                                    }}
                                >
                                    Choose the survivor
                                </span>
                                <span
                                    className="text-[var(--cpm-text-secondary)] text-pretty"
                                    style={{ fontSize: 13, fontWeight: 600 }}
                                >
                                    Pick the record that stays. The other is retired, and you then
                                    decide what happens to tenant records pointing at it. Nothing is
                                    pre-selected — this is your call.
                                </span>
                            </div>

                            <div
                                className="grid grid-cols-1 lg:grid-cols-2"
                                style={{ gap: 16, alignItems: 'start' }}
                            >
                                {sides.map((side) => {
                                    const selected = survivorId === side.id;
                                    const retired = survivorId != null && survivorId !== side.id;
                                    const aliases = aliasesOf(side.details);
                                    const status = String(side.details?.record?.status ?? '');
                                    const children = childrenFor(side.details);
                                    const rows = metaRowsFor(side.details);
                                    const kidsOpen =
                                        openChildren[side.id] === undefined
                                            ? level === 'product'
                                            : !!openChildren[side.id];
                                    const showChildren = level === 'brand' || level === 'product';

                                    return (
                                        <div
                                            key={side.id}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => pickSurvivor(side.id)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    pickSurvivor(side.id);
                                                }
                                            }}
                                            className="flex flex-col bg-[var(--cpm-surface-card)] cursor-pointer transition-all"
                                            style={{
                                                padding: '18px 20px',
                                                borderRadius: 16,
                                                gap: 12,
                                                border: selected
                                                    ? '1.5px solid var(--cpm-brand-purple)'
                                                    : '1.5px solid var(--cpm-border-default)',
                                                boxShadow: selected
                                                    ? '0 0 0 3px rgba(110,100,240,.28)'
                                                    : '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                                                opacity: retired ? 0.72 : 1,
                                            }}
                                        >
                                            <div
                                                className="flex items-center"
                                                style={{ gap: 8, flexWrap: 'wrap' }}
                                            >
                                                <span
                                                    className="inline-flex items-center rounded-full uppercase bg-[var(--cpm-surface-skeleton)] text-[var(--cpm-text-secondary)]"
                                                    style={{
                                                        padding: '3px 9px',
                                                        fontSize: 10,
                                                        fontWeight: 800,
                                                        letterSpacing: '0.06em',
                                                    }}
                                                >
                                                    {levelUpper(level)}
                                                </span>
                                                {status ? <CpmStatusChip status={status} /> : null}
                                                {survivorId != null ? (
                                                    <span
                                                        className="inline-flex items-center ml-auto shrink-0"
                                                        style={{
                                                            gap: 5,
                                                            padding: '3px 10px',
                                                            borderRadius: 999,
                                                            fontSize: 10,
                                                            fontWeight: 800,
                                                            letterSpacing: '0.06em',
                                                            background: selected
                                                                ? 'var(--cpm-brand-purple)'
                                                                : 'var(--cpm-shortfall-bg)',
                                                            border: selected
                                                                ? undefined
                                                                : '1px solid var(--cpm-shortfall-border)',
                                                            color: selected
                                                                ? '#fff'
                                                                : 'var(--cpm-shortfall-text)',
                                                        }}
                                                    >
                                                        {selected ? 'survivor' : 'retired'}
                                                    </span>
                                                ) : null}
                                            </div>

                                            <div
                                                className="text-[var(--cpm-text-primary)] text-pretty"
                                                style={{
                                                    fontSize: 17,
                                                    fontWeight: 800,
                                                    letterSpacing: '-0.02em',
                                                    lineHeight: 1.25,
                                                }}
                                            >
                                                {nameOf(side.details, side.id)}
                                            </div>

                                            <div className="flex flex-col border-t border-[var(--cpm-border-default)]">
                                                {rows.map((r, i) => {
                                                    const isConflict =
                                                        !!r.conflictable && conflictKeys.has(r.key);
                                                    const valueColor = isConflict
                                                        ? selected
                                                            ? 'var(--cpm-brand-navy)'
                                                            : 'var(--cpm-shortfall-text-med)'
                                                        : r.muted
                                                          ? 'var(--cpm-text-tertiary)'
                                                          : 'var(--cpm-text-primary)';
                                                    return (
                                                        <div
                                                            key={r.key}
                                                            className="flex items-center justify-between"
                                                            style={{
                                                                gap: 12,
                                                                padding: '8px 0',
                                                                borderBottom:
                                                                    i < rows.length - 1
                                                                        ? '1px solid var(--cpm-border-default)'
                                                                        : undefined,
                                                            }}
                                                        >
                                                            <span
                                                                className="uppercase text-[var(--cpm-text-tertiary)] shrink-0"
                                                                style={{
                                                                    fontSize: 10.5,
                                                                    fontWeight: 800,
                                                                    letterSpacing: '0.06em',
                                                                }}
                                                            >
                                                                {r.label}
                                                            </span>
                                                            <span
                                                                className="min-w-0 text-right break-words [overflow-wrap:anywhere] tabular"
                                                                style={{
                                                                    fontSize: 12.5,
                                                                    fontWeight: 800,
                                                                    color: valueColor,
                                                                }}
                                                            >
                                                                {r.value}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {showChildren ? (
                                                <div
                                                    className="flex flex-col border border-[var(--cpm-border-default)] bg-[var(--cpm-surface-subtle)] overflow-hidden"
                                                    style={{ borderRadius: 12 }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setOpenChildren((prev) => ({
                                                                ...prev,
                                                                [side.id]: !prev[side.id],
                                                            }))
                                                        }
                                                        className="flex items-center w-full border-0 bg-transparent cursor-pointer text-left"
                                                        style={{
                                                            gap: 10,
                                                            padding: '11px 13px',
                                                        }}
                                                    >
                                                        <span
                                                            className="uppercase text-[var(--cpm-text-secondary)]"
                                                            style={{
                                                                fontSize: 10.5,
                                                                fontWeight: 800,
                                                                letterSpacing: '0.06em',
                                                            }}
                                                        >
                                                            {childLabel}
                                                        </span>
                                                        <span
                                                            className="inline-flex items-center rounded-full bg-white border border-[var(--cpm-border-default)] text-[var(--cpm-text-secondary)] tabular"
                                                            style={{
                                                                padding: '2px 8px',
                                                                fontSize: 11,
                                                                fontWeight: 800,
                                                            }}
                                                        >
                                                            {formatCpmCount(children.length)}
                                                        </span>
                                                        <span
                                                            className="ml-auto inline-flex text-[var(--cpm-text-tertiary)]"
                                                            style={{
                                                                transform: kidsOpen
                                                                    ? 'rotate(180deg)'
                                                                    : undefined,
                                                                transition: 'transform 150ms ease',
                                                            }}
                                                        >
                                                            <svg
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2.4"
                                                            >
                                                                <path
                                                                    d="m6 9 6 6 6-6"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                            </svg>
                                                        </span>
                                                    </button>
                                                    {kidsOpen ? (
                                                        <div className="flex flex-col bg-white border-t border-[var(--cpm-border-default)]">
                                                            {children.length === 0 ? (
                                                                <span
                                                                    className="text-[var(--cpm-text-tertiary)] text-pretty"
                                                                    style={{
                                                                        padding: 13,
                                                                        fontSize: 12,
                                                                        fontWeight: 700,
                                                                    }}
                                                                >
                                                                    {level === 'brand'
                                                                        ? 'No catalog products are filed under this brand yet.'
                                                                        : 'No variants exist under this product yet.'}
                                                                </span>
                                                            ) : (
                                                                children.map((ch, idx) => (
                                                                    <div
                                                                        key={ch.id}
                                                                        className="flex items-center"
                                                                        style={{
                                                                            gap: 10,
                                                                            padding: '10px 13px',
                                                                            borderBottom:
                                                                                idx <
                                                                                children.length - 1
                                                                                    ? '1px solid var(--cpm-border-default)'
                                                                                    : undefined,
                                                                        }}
                                                                    >
                                                                        <span className="flex-1 min-w-0 truncate text-[12.5px] font-bold text-[var(--cpm-text-primary)]">
                                                                            {ch.name}
                                                                        </span>
                                                                        {ch.volume ? (
                                                                            <span className="shrink-0 text-[11.5px] font-extrabold tabular text-[var(--cpm-text-secondary)]">
                                                                                {ch.volume}
                                                                            </span>
                                                                        ) : null}
                                                                        <CpmStatusChip
                                                                            status={ch.status}
                                                                        />
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            ) : null}

                                            <div className="flex flex-col" style={{ gap: 6 }}>
                                                <span
                                                    className="uppercase text-[var(--cpm-text-tertiary)]"
                                                    style={{
                                                        fontSize: 10,
                                                        fontWeight: 800,
                                                        letterSpacing: '0.07em',
                                                    }}
                                                >
                                                    {aliasLabel}
                                                </span>
                                                <div className="flex flex-col" style={{ gap: 4 }}>
                                                    {aliases.length === 0 ? (
                                                        <span
                                                            className="text-[var(--cpm-text-tertiary)]"
                                                            style={{
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            None recorded
                                                        </span>
                                                    ) : (
                                                        aliases.map((alias) => (
                                                            <span
                                                                key={alias}
                                                                className="text-[var(--cpm-text-secondary)] text-pretty"
                                                                style={{
                                                                    fontSize: 12.5,
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                {alias}
                                                            </span>
                                                        ))
                                                    )}
                                                </div>
                                            </div>

                                            <div
                                                className="inline-flex items-center justify-center"
                                                style={{
                                                    gap: 7,
                                                    height: 40,
                                                    marginTop: 2,
                                                    borderRadius: 10,
                                                    fontSize: 13,
                                                    fontWeight: 800,
                                                    letterSpacing: '-0.01em',
                                                    background: selected
                                                        ? 'var(--cpm-brand-navy)'
                                                        : '#fff',
                                                    color: selected
                                                        ? '#fff'
                                                        : 'var(--cpm-brand-navy)',
                                                    border: selected
                                                        ? undefined
                                                        : '1.5px solid var(--cpm-border-strong)',
                                                }}
                                            >
                                                {selected ? (
                                                    <svg
                                                        width="15"
                                                        height="15"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2.6"
                                                    >
                                                        <path
                                                            d="M20 6 9 17l-5-5"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                ) : null}
                                                {selected
                                                    ? 'Survivor — this record stays'
                                                    : 'Designate as survivor'}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {level === 'variant' && sideA && sideB ? (
                            <BarcodeOverlap
                                sideA={sideA}
                                sideB={sideB}
                                idA={idA}
                                idB={idB}
                                survivorId={survivorId}
                            />
                        ) : null}

                        {sideA && sideB ? (
                            <BarcodeThirdPartyWarning claims={leftoverClaims} />
                        ) : null}

                        {level === 'product' && sideA && sideB ? (
                            <ProductVolumeCollisions
                                sideA={sideA}
                                sideB={sideB}
                                idA={idA}
                                idB={idB}
                                survivorId={survivorId}
                                volumeChoices={volumeChoices}
                                volStateChoices={volStateChoices}
                                openVolStates={openVolStates}
                                onPickVolume={(volumeKey, variantId) =>
                                    setVolumeChoices((prev) => ({
                                        ...prev,
                                        [volumeKey]: variantId,
                                    }))
                                }
                                onPickVolState={(volumeKey, state, variantId) =>
                                    setVolStateChoices((prev) => ({
                                        ...prev,
                                        [volumeKey]: {
                                            ...(prev[volumeKey] ?? {}),
                                            [state]: variantId,
                                        },
                                    }))
                                }
                                onToggleVolStates={(volumeKey) =>
                                    setOpenVolStates((prev) => ({
                                        ...prev,
                                        [volumeKey]: prev[volumeKey] === false,
                                    }))
                                }
                            />
                        ) : null}

                        {level === 'variant' && sideA && sideB ? (
                            <VariantStateCollisions
                                sideA={sideA}
                                sideB={sideB}
                                idA={idA}
                                idB={idB}
                                survivorId={survivorId}
                                stateChoices={stateChoices}
                                onPickState={(state, sideId) =>
                                    setStateChoices((prev) => ({
                                        ...prev,
                                        [state]: sideId,
                                    }))
                                }
                            />
                        ) : null}

                        {level === 'brand' && sideA && sideB ? (
                            <BrandProductCollisions
                                sideA={sideA}
                                sideB={sideB}
                                brandNameA={nameOf(sideA, idA)}
                                brandNameB={nameOf(sideB, idB)}
                                pairs={brandPairs}
                                productChoices={productChoices}
                                volumeChoicesByPair={brandVolumeChoices}
                                volStateChoicesByPair={brandVolStateChoices}
                                openProdVol={openProdVol}
                                openVolStatesByPair={openBrandVolStates}
                                onPickProduct={(pairKey, productId) =>
                                    setProductChoices((prev) => ({
                                        ...prev,
                                        [pairKey]: productId,
                                    }))
                                }
                                onPickVolume={(pairKey, volumeKey, variantId) =>
                                    setBrandVolumeChoices((prev) => ({
                                        ...prev,
                                        [pairKey]: {
                                            ...(prev[pairKey] ?? {}),
                                            [volumeKey]: variantId,
                                        },
                                    }))
                                }
                                onPickVolState={(pairKey, volumeKey, state, variantId) =>
                                    setBrandVolStateChoices((prev) => ({
                                        ...prev,
                                        [pairKey]: {
                                            ...(prev[pairKey] ?? {}),
                                            [volumeKey]: {
                                                ...(prev[pairKey]?.[volumeKey] ?? {}),
                                                [state]: variantId,
                                            },
                                        },
                                    }))
                                }
                                onToggleVol={(pairKey) =>
                                    setOpenProdVol((prev) => ({
                                        ...prev,
                                        [pairKey]: prev[pairKey] === false,
                                    }))
                                }
                                onToggleVolStates={(pairKey, volumeKey) =>
                                    setOpenBrandVolStates((prev) => ({
                                        ...prev,
                                        [pairKey]: {
                                            ...(prev[pairKey] ?? {}),
                                            [volumeKey]: prev[pairKey]?.[volumeKey] === false,
                                        },
                                    }))
                                }
                            />
                        ) : null}

                        <div
                            className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] flex flex-col"
                            style={{
                                borderRadius: 16,
                                padding: '20px 22px',
                                gap: 16,
                                boxShadow:
                                    '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                            }}
                        >
                            <div className="flex items-baseline flex-wrap" style={{ gap: 12 }}>
                                <span
                                    className="uppercase text-[var(--cpm-text-secondary)]"
                                    style={{
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.08em',
                                    }}
                                >
                                    What merging will do
                                </span>
                                <span
                                    className="text-[var(--cpm-text-secondary)]"
                                    style={{ fontSize: 13, fontWeight: 600 }}
                                >
                                    {impact
                                        ? 'The survivor keeps everything below; the retired record is preserved for audit.'
                                        : 'Grounded in exactly what a merge does — shown once you pick a survivor.'}
                                </span>
                            </div>

                            {!impact ? (
                                <div
                                    className="flex items-center border border-dashed border-[var(--cpm-border-strong)] bg-[var(--cpm-surface-subtle)]"
                                    style={{
                                        gap: 11,
                                        padding: '16px 18px',
                                        borderRadius: 12,
                                    }}
                                >
                                    <span
                                        className="text-[var(--cpm-text-secondary)] text-pretty"
                                        style={{ fontSize: 13, fontWeight: 700 }}
                                    >
                                        Choose which record survives above, and the exact impact —
                                        combined fields, the tenant-document decision, and the
                                        retirement marker — appears here.
                                    </span>
                                </div>
                            ) : (
                                <div className="flex flex-col" style={{ gap: 16 }}>
                                    <div
                                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                                        style={{ gap: 12 }}
                                    >
                                        {impact.tiles.map((tile) => (
                                            <div
                                                key={tile.label}
                                                className="flex flex-col bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-default)]"
                                                style={{
                                                    gap: 6,
                                                    padding: '14px 16px',
                                                    borderRadius: 12,
                                                }}
                                            >
                                                <span
                                                    className="text-[var(--cpm-brand-navy)] tabular"
                                                    style={{
                                                        fontSize: 22,
                                                        fontWeight: 800,
                                                        letterSpacing: '-0.025em',
                                                    }}
                                                >
                                                    {tile.value}
                                                </span>
                                                <span
                                                    className="text-[var(--cpm-text-primary)]"
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 800,
                                                        letterSpacing: '-0.01em',
                                                    }}
                                                >
                                                    {tile.label}
                                                </span>
                                                <span
                                                    className="text-[var(--cpm-text-secondary)] text-pretty"
                                                    style={{
                                                        fontSize: 11.5,
                                                        fontWeight: 600,
                                                        lineHeight: 1.45,
                                                    }}
                                                >
                                                    {tile.sub}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    <div
                                        className="flex flex-col border border-[var(--cpm-border-default)] bg-[var(--cpm-surface-subtle)]"
                                        style={{ gap: 10, padding: '15px 16px', borderRadius: 12 }}
                                    >
                                        <div
                                            className="flex items-baseline flex-wrap"
                                            style={{ gap: 12 }}
                                        >
                                            <span
                                                className="uppercase text-[var(--cpm-text-secondary)]"
                                                style={{
                                                    fontSize: 10.5,
                                                    fontWeight: 800,
                                                    letterSpacing: '0.08em',
                                                }}
                                            >
                                                {impact.aliasPreviewTitle}
                                            </span>
                                            <span
                                                className="text-[var(--cpm-text-secondary)] text-pretty"
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    lineHeight: 1.45,
                                                }}
                                            >
                                                {impact.aliasPreviewNote}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap" style={{ gap: 7 }}>
                                            {impact.aliasPreview.map((a) => (
                                                <span
                                                    key={a.text}
                                                    className="inline-flex items-center"
                                                    style={{
                                                        gap: 8,
                                                        padding: '5px 11px',
                                                        borderRadius: 999,
                                                        fontSize: 12,
                                                        fontWeight: 800,
                                                        letterSpacing: '-0.005em',
                                                        background: a.isNew
                                                            ? 'var(--cpm-purple-tint-5)'
                                                            : '#fff',
                                                        border: a.isNew
                                                            ? '1.5px solid var(--cpm-purple-accent-border)'
                                                            : '1px solid var(--cpm-border-default)',
                                                        color: a.isNew
                                                            ? 'var(--cpm-brand-navy)'
                                                            : 'var(--cpm-text-primary)',
                                                    }}
                                                >
                                                    {a.text}
                                                    {a.isNew ? (
                                                        <span
                                                            className="font-mono uppercase text-[var(--cpm-brand-purple)] whitespace-nowrap"
                                                            style={{
                                                                fontSize: 9,
                                                                fontWeight: 800,
                                                                letterSpacing: '0.07em',
                                                            }}
                                                        >
                                                            {a.tag}
                                                        </span>
                                                    ) : null}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    {(
                                        [
                                            {
                                                title: impact.absorbTitle,
                                                body: impact.absorbBody,
                                                icon: 'merge' as const,
                                            },
                                            {
                                                title: impact.tenantRowTitle,
                                                body: impact.tenantRowBody,
                                                icon: 'repoint' as const,
                                            },
                                            {
                                                title: `${impact.retiredName} is retired, not deleted`,
                                                body: `It is kept in the catalog and permanently marked “Merged into ${impact.survivorName}” for audit — it never disappears from the database, it just stops being a live record.`,
                                                icon: 'archive' as const,
                                            },
                                        ] as const
                                    ).map((row) => (
                                        <div
                                            key={row.title}
                                            className="flex items-start border-t border-[var(--cpm-border-default)]"
                                            style={{ gap: 12, paddingTop: 14 }}
                                        >
                                            <span
                                                className="inline-flex items-center justify-center shrink-0"
                                                style={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: 8,
                                                    background:
                                                        row.icon === 'archive'
                                                            ? 'var(--cpm-surface-skeleton)'
                                                            : 'var(--cpm-purple-tint-13)',
                                                    color:
                                                        row.icon === 'archive'
                                                            ? 'var(--cpm-text-secondary)'
                                                            : 'var(--cpm-brand-purple)',
                                                }}
                                            >
                                                {row.icon === 'merge' ? (
                                                    <MergeIcon size={15} />
                                                ) : row.icon === 'repoint' ? (
                                                    <svg
                                                        width="15"
                                                        height="15"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                    >
                                                        <path d="M5 9H3v6h2" />
                                                        <path d="M19 15h2V9h-2" />
                                                        <path d="M8 12h8" />
                                                        <path d="m13 9 3 3-3 3" />
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        width="15"
                                                        height="15"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                    >
                                                        <rect
                                                            width="20"
                                                            height="5"
                                                            x="2"
                                                            y="3"
                                                            rx="1"
                                                        />
                                                        <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
                                                        <path d="M10 12h4" />
                                                    </svg>
                                                )}
                                            </span>
                                            <div
                                                className="flex flex-col min-w-0"
                                                style={{ gap: 2 }}
                                            >
                                                <span
                                                    className="text-[var(--cpm-text-primary)] text-pretty"
                                                    style={{
                                                        fontSize: 13,
                                                        fontWeight: 800,
                                                        letterSpacing: '-0.01em',
                                                    }}
                                                >
                                                    {row.title}
                                                </span>
                                                <span
                                                    className="text-[var(--cpm-text-secondary)] text-pretty"
                                                    style={{
                                                        fontSize: 12.5,
                                                        fontWeight: 600,
                                                        lineHeight: 1.5,
                                                    }}
                                                >
                                                    {row.body}
                                                </span>
                                            </div>
                                        </div>
                                    ))}

                                    <div
                                        className="flex flex-col border"
                                        style={{
                                            gap: 12,
                                            padding: '15px 17px',
                                            borderRadius: 12,
                                            borderColor:
                                                !tenantActionNeeded || tenantAction
                                                    ? 'var(--cpm-border-default)'
                                                    : 'var(--cpm-warning-border)',
                                            background:
                                                !tenantActionNeeded || tenantAction
                                                    ? 'var(--cpm-surface-subtle)'
                                                    : 'var(--cpm-warning-bg)',
                                        }}
                                    >
                                        <div
                                            className="flex items-center flex-wrap"
                                            style={{ gap: 10 }}
                                        >
                                            <span
                                                className="uppercase text-[var(--cpm-text-secondary)]"
                                                style={{
                                                    fontSize: 10.5,
                                                    fontWeight: 800,
                                                    letterSpacing: '0.08em',
                                                }}
                                            >
                                                Tenant documents
                                            </span>
                                            <span
                                                className="inline-flex items-center rounded-full uppercase"
                                                style={{
                                                    padding: '3px 9px',
                                                    fontSize: 9.5,
                                                    fontWeight: 800,
                                                    letterSpacing: '0.06em',
                                                    background: !tenantActionNeeded
                                                        ? 'var(--cpm-surface-skeleton)'
                                                        : tenantAction
                                                          ? 'var(--cpm-success-bg)'
                                                          : 'var(--cpm-warning-bg)',
                                                    border: !tenantActionNeeded
                                                        ? undefined
                                                        : `1px solid ${tenantAction ? 'var(--cpm-success-border)' : 'var(--cpm-warning-border)'}`,
                                                    color: !tenantActionNeeded
                                                        ? 'var(--cpm-text-secondary)'
                                                        : tenantAction
                                                          ? 'var(--cpm-success-text-light)'
                                                          : 'var(--cpm-warning-text)',
                                                }}
                                            >
                                                {!tenantActionNeeded
                                                    ? 'Nothing to decide'
                                                    : tenantAction
                                                      ? 'Resolved'
                                                      : 'Decision needed'}
                                            </span>
                                            <span
                                                className="text-[var(--cpm-text-primary)] text-pretty"
                                                style={{ fontSize: 12.5, fontWeight: 700 }}
                                            >
                                                {impact.tenantEffect}
                                            </span>
                                        </div>
                                        {tenantActionNeeded ? (
                                            <>
                                                <div
                                                    className="grid grid-cols-1 md:grid-cols-2"
                                                    style={{ gap: 10 }}
                                                >
                                                    {(
                                                        [
                                                            {
                                                                value: CPM_TENANT_MERGE_ACTION.REPOINT,
                                                                title: 'Re-point to survivor',
                                                                body: `Those tenant records point at ${impact.survivorName} from now on — the previous always-on behavior.`,
                                                            },
                                                            {
                                                                value: CPM_TENANT_MERGE_ACTION.UNLINK,
                                                                title: 'Unlink',
                                                                body: 'Their master pointer is cleared instead. They become unlinked and the next daily linking run re-evaluates them.',
                                                            },
                                                        ] as const
                                                    ).map((opt) => (
                                                        <button
                                                            key={opt.value}
                                                            type="button"
                                                            onClick={() =>
                                                                setTenantAction(opt.value)
                                                            }
                                                            className="text-left flex flex-col bg-white"
                                                            style={{
                                                                gap: 8,
                                                                padding: '13px 14px',
                                                                borderRadius: 12,
                                                                border:
                                                                    tenantAction === opt.value
                                                                        ? '1.5px solid var(--cpm-brand-purple)'
                                                                        : '1.5px solid var(--cpm-border-default)',
                                                                boxShadow:
                                                                    tenantAction === opt.value
                                                                        ? '0 0 0 3px rgba(110,100,240,.20)'
                                                                        : undefined,
                                                                background:
                                                                    tenantAction === opt.value
                                                                        ? 'rgba(110,100,240,.07)'
                                                                        : '#fff',
                                                            }}
                                                        >
                                                            <span
                                                                className="text-[var(--cpm-text-primary)]"
                                                                style={{
                                                                    fontSize: 12.5,
                                                                    fontWeight: 800,
                                                                }}
                                                            >
                                                                {opt.title}
                                                            </span>
                                                            <span
                                                                className="text-[var(--cpm-text-secondary)] text-pretty"
                                                                style={{
                                                                    fontSize: 12,
                                                                    fontWeight: 600,
                                                                    lineHeight: 1.5,
                                                                }}
                                                            >
                                                                {opt.body}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                                <span
                                                    className="text-[var(--cpm-text-secondary)] text-pretty"
                                                    style={{
                                                        fontSize: 11.5,
                                                        fontWeight: 600,
                                                        lineHeight: 1.5,
                                                    }}
                                                >
                                                    This choice governs tenant document pointers
                                                    only. The master-side merge — the alias union,
                                                    child re-parenting, the “Merged into” marker on
                                                    the retiring record
                                                    {level === 'variant'
                                                        ? ', and barcode registry handling'
                                                        : ''}{' '}
                                                    — happens either way. Unlink does not cancel the
                                                    merge.
                                                </span>
                                            </>
                                        ) : null}
                                    </div>

                                    {impact.conflicts.length > 0 ? (
                                        <div
                                            className="flex flex-col border border-[var(--cpm-warning-border)] bg-[var(--cpm-warning-bg)]"
                                            style={{
                                                gap: 12,
                                                padding: '16px 18px',
                                                borderRadius: 12,
                                            }}
                                        >
                                            <div className="flex items-center" style={{ gap: 9 }}>
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="var(--cpm-warning-icon)"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="shrink-0"
                                                    style={{ maxWidth: 'none', display: 'block' }}
                                                    aria-hidden
                                                >
                                                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                                    <path d="M12 9v4" />
                                                    <path d="M12 17h.01" />
                                                </svg>
                                                <span
                                                    className="text-[var(--cpm-warning-text)]"
                                                    style={{
                                                        fontSize: 13,
                                                        fontWeight: 800,
                                                        letterSpacing: '-0.01em',
                                                    }}
                                                >
                                                    These fields differ — the survivor&apos;s value
                                                    is kept
                                                </span>
                                            </div>
                                            <div
                                                className="overflow-hidden bg-white border border-[var(--cpm-warning-border)]"
                                                style={{ borderRadius: 10 }}
                                            >
                                                <div
                                                    className="grid uppercase items-center bg-[var(--cpm-surface-subtle)] text-[var(--cpm-text-primary)] border-b border-[var(--cpm-border-default)]"
                                                    style={{
                                                        gridTemplateColumns:
                                                            '120px minmax(0,1fr) 20px minmax(0,1fr)',
                                                        gap: 12,
                                                        padding: '8px 13px',
                                                        fontSize: 9.5,
                                                        fontWeight: 800,
                                                        letterSpacing: '0.07em',
                                                    }}
                                                >
                                                    <span>Field</span>
                                                    <span>Retired keeps</span>
                                                    <span />
                                                    <span>Survivor keeps</span>
                                                </div>
                                                {impact.conflicts.map((c, idx) => (
                                                    <div
                                                        key={c.field}
                                                        className="grid items-center"
                                                        style={{
                                                            gridTemplateColumns:
                                                                '120px minmax(0,1fr) 20px minmax(0,1fr)',
                                                            gap: 12,
                                                            padding: '9px 13px',
                                                            borderTop:
                                                                idx === 0
                                                                    ? undefined
                                                                    : '1px solid var(--cpm-border-default)',
                                                        }}
                                                    >
                                                        <span
                                                            className="uppercase text-[var(--cpm-text-tertiary)]"
                                                            style={{
                                                                fontSize: 11,
                                                                fontWeight: 800,
                                                                letterSpacing: '0.05em',
                                                            }}
                                                        >
                                                            {c.field}
                                                        </span>
                                                        <span
                                                            className="min-w-0 truncate text-[var(--cpm-text-tertiary)]"
                                                            style={{
                                                                fontSize: 12.5,
                                                                fontWeight: 700,
                                                                textDecoration: 'line-through',
                                                                textDecorationColor:
                                                                    'var(--cpm-border-strong)',
                                                            }}
                                                        >
                                                            {c.retired}
                                                        </span>
                                                        <svg
                                                            width="14"
                                                            height="14"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="var(--cpm-text-tertiary)"
                                                            strokeWidth="2.2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            className="shrink-0 justify-self-center"
                                                            style={{
                                                                maxWidth: 'none',
                                                                display: 'block',
                                                            }}
                                                            aria-hidden
                                                        >
                                                            <path d="M5 12h14" />
                                                            <path d="m12 5 7 7-7 7" />
                                                        </svg>
                                                        <span
                                                            className="min-w-0 truncate text-[var(--cpm-brand-navy)]"
                                                            style={{
                                                                fontSize: 12.5,
                                                                fontWeight: 800,
                                                            }}
                                                        >
                                                            {c.survivor}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                            <span
                                                className="text-pretty"
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    lineHeight: 1.5,
                                                    color: 'var(--cpm-warning-text)',
                                                }}
                                            >
                                                You don&apos;t have to reconcile these here.
                                                Whatever isn&apos;t chosen stays editable on the
                                                survivor afterwards in the{' '}
                                                <Link
                                                    to={
                                                        level === 'brand'
                                                            ? '/central-product-master/master-editor?level=brands'
                                                            : level === 'product'
                                                              ? '/central-product-master/master-editor?level=catalog&view=products'
                                                              : '/central-product-master/master-editor?level=catalog&view=variants'
                                                    }
                                                    className="font-extrabold text-[var(--cpm-warning-text)]"
                                                    style={{
                                                        borderBottom:
                                                            '1px solid var(--cpm-warning-border)',
                                                    }}
                                                >
                                                    Master Editor
                                                </Link>
                                                .
                                            </span>
                                        </div>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </CpmModalBody>

            <CpmModalFooter layout="stack" className="!px-7 !py-[14px]">
                {/* Design: stacked footer — verdict row, then action row right-aligned. */}
                <div className="flex flex-col min-w-0 w-full" style={{ gap: 2 }}>
                    <span
                        style={{
                            fontSize: 13.5,
                            fontWeight: 800,
                            letterSpacing: '-0.01em',
                            color:
                                verdict.tone === 'purple'
                                    ? 'var(--cpm-brand-purple)'
                                    : 'var(--cpm-brand-navy)',
                        }}
                    >
                        {verdict.label}
                    </span>
                    <span
                        className="text-[var(--cpm-text-secondary)] text-pretty"
                        style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.4 }}
                    >
                        {verdict.hint}
                    </span>
                </div>
                <div className="flex flex-wrap items-center justify-end w-full" style={{ gap: 14 }}>
                    {impact ? (
                        <button
                            type="button"
                            onClick={() => setAck((v) => !v)}
                            className="inline-flex items-center"
                            style={{
                                gap: 10,
                                maxWidth: 340,
                                padding: '7px 13px 7px 11px',
                                borderRadius: 10,
                                border: `1.5px solid ${ack ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)'}`,
                                background: ack ? 'rgba(110,100,240,.07)' : '#fff',
                                transition: 'all 150ms ease',
                            }}
                        >
                            <span
                                className="inline-flex items-center justify-center shrink-0"
                                style={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: 5,
                                    border: `1.5px solid ${ack ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)'}`,
                                    background: ack ? 'var(--cpm-brand-purple)' : '#fff',
                                }}
                            >
                                {ack ? (
                                    <svg
                                        width="11"
                                        height="11"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#fff"
                                        strokeWidth="3.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        style={{ maxWidth: 'none', display: 'block' }}
                                        aria-hidden
                                    >
                                        <path d="M20 6 9 17l-5-5" />
                                    </svg>
                                ) : null}
                            </span>
                            <span
                                style={{
                                    fontSize: 12,
                                    fontWeight: 800,
                                    lineHeight: 1.35,
                                    textAlign: 'left',
                                    color: ack
                                        ? 'var(--cpm-brand-navy)'
                                        : 'var(--cpm-text-secondary)',
                                }}
                            >
                                {`I understand ${impact.retiredName} will be retired and marked merged`}
                            </span>
                        </button>
                    ) : null}
                    <CpmButton variant="outline" size="lg" onClick={onClose}>
                        Cancel
                    </CpmButton>
                    <CpmButton
                        variant="danger"
                        size="lg"
                        onClick={confirm}
                        disabled={!canConfirm}
                        isLoading={phase === 'working'}
                        leadingIcon={<MergeIcon size={16} />}
                    >
                        {confirmLabel}
                    </CpmButton>
                </div>
            </CpmModalFooter>

            {phase === 'working' || phase === 'done' || phase === 'error' ? (
                <div
                    className="fixed inset-0 z-[80] flex items-center justify-center"
                    style={{
                        padding: '32px 24px',
                        background: 'rgba(14,8,88,.34)',
                        backdropFilter: 'blur(3px)',
                    }}
                >
                    <div
                        className="w-full bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] overflow-hidden"
                        style={{
                            maxWidth: 520,
                            borderRadius: 16,
                            boxShadow:
                                '0 1px 2px rgba(14,8,88,.06), 0 30px 70px -18px rgba(14,8,88,.45)',
                        }}
                    >
                        {phase === 'working' ? (
                            <div
                                className="flex flex-col items-center text-center"
                                style={{ padding: '52px 30px', gap: 15 }}
                            >
                                <span
                                    className="inline-flex items-center justify-center bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                                    style={{ width: 40, height: 40, borderRadius: 11 }}
                                >
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.2"
                                        className="animate-spin"
                                    >
                                        <path d="M12 2v4M12 18v4M4.9 4.9l2.9 2.9M16.2 16.2l2.9 2.9M2 12h4M18 12h4M4.9 19.1l2.9-2.9M16.2 7.8l2.9-2.9" />
                                    </svg>
                                </span>
                                <span
                                    className="text-[var(--cpm-brand-navy)]"
                                    style={{ fontSize: 17, fontWeight: 800 }}
                                >
                                    Merging records…
                                </span>
                                <span
                                    className="text-[var(--cpm-text-secondary)] text-pretty"
                                    style={{
                                        fontSize: 12.5,
                                        fontWeight: 600,
                                        lineHeight: 1.5,
                                        maxWidth: 360,
                                    }}
                                >
                                    Combining fields onto the survivor and marking the retired
                                    record. Don&apos;t close this window.
                                </span>
                            </div>
                        ) : phase === 'error' ? (
                            <div
                                className="flex flex-col items-center text-center"
                                style={{ padding: '40px 30px 26px', gap: 13 }}
                            >
                                <span
                                    className="inline-flex items-center justify-center bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)]"
                                    style={{ width: 56, height: 56, borderRadius: 14 }}
                                >
                                    <svg
                                        width="26"
                                        height="26"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.2"
                                    >
                                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                        <path d="M12 9v4M12 17h.01" />
                                    </svg>
                                </span>
                                <span
                                    className="text-[var(--cpm-brand-navy)]"
                                    style={{
                                        fontSize: 19,
                                        fontWeight: 800,
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    The merge did not go through
                                </span>
                                <span
                                    className="text-[var(--cpm-text-secondary)] text-pretty"
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        lineHeight: 1.5,
                                        maxWidth: 400,
                                    }}
                                >
                                    {mergeError ||
                                        'The catalog service did not complete the merge. Both records are unchanged and still live — nothing was retired.'}
                                </span>
                                <div
                                    className="flex items-center flex-wrap justify-center"
                                    style={{ gap: 10, marginTop: 8 }}
                                >
                                    <CpmButton
                                        variant="outline"
                                        size="modal"
                                        onClick={() => setPhase('choose')}
                                    >
                                        Back to the decision
                                    </CpmButton>
                                    <CpmButton variant="navy" size="modal" onClick={confirm}>
                                        Try again
                                    </CpmButton>
                                </div>
                            </div>
                        ) : (
                            <div
                                className="flex flex-col items-center text-center"
                                style={{ padding: '40px 30px 30px', gap: 13 }}
                            >
                                <span
                                    className="inline-flex items-center justify-center bg-[var(--cpm-success-bg)] border border-[var(--cpm-success-border)] text-[var(--cpm-success-text-light)]"
                                    style={{ width: 56, height: 56, borderRadius: 14 }}
                                >
                                    <svg
                                        width="26"
                                        height="26"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.4"
                                    >
                                        <path
                                            d="M20 6 9 17l-5-5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </span>
                                <span
                                    className="text-[var(--cpm-brand-navy)] text-pretty"
                                    style={{
                                        fontSize: 20,
                                        fontWeight: 800,
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    {impact
                                        ? `${impact.survivorName} is now the single record`
                                        : 'Records merged'}
                                </span>
                                <span
                                    className="text-[var(--cpm-text-secondary)] text-pretty"
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        lineHeight: 1.55,
                                        maxWidth: 400,
                                    }}
                                >
                                    {impact
                                        ? (level === 'brand'
                                              ? impact.collidingPairCount > 0
                                                  ? `It keeps the combined aliases from both brands. ${formatCpmCount(impact.movingOnlyCount)} product${impact.movingOnlyCount === 1 ? '' : 's'} filed only under ${impact.retiredName} move to it, and for each same-name product the one you picked stays while the other is retired into it. ${impact.retiredName} is retired and marked “Merged into ${impact.survivorName}.”`
                                                  : `It keeps the combined aliases from both brands, and the products under ${impact.retiredName} are re-parented to it. ${impact.retiredName} is retired and marked “Merged into ${impact.survivorName}.”`
                                              : level === 'product'
                                                ? `It keeps the combined alternate names, and the variants under ${impact.retiredName} are re-parented to it. ${impact.retiredName} is retired and marked “Merged into ${impact.survivorName}.”`
                                                : `It carries the combined barcodes and alternate names from both records. ${impact.retiredName} is retired and marked “Merged into ${impact.survivorName}.”`) +
                                          (impact.tenantCount > 0
                                              ? tenantAction === CPM_TENANT_MERGE_ACTION.UNLINK
                                                  ? ` The ${formatCpmCount(impact.tenantCount)} store-brand record${impact.tenantCount === 1 ? '' : 's'} that pointed at it are being unlinked now and will be re-evaluated by the next daily linking run.`
                                                  : ` The ${formatCpmCount(impact.tenantCount)} store-brand record${impact.tenantCount === 1 ? '' : 's'} pointing at it are being re-pointed to ${impact.survivorName} now and will update within moments.`
                                              : '')
                                        : 'The merge completed successfully.'}
                                </span>
                                <div
                                    className="flex items-center flex-wrap justify-center"
                                    style={{ gap: 10, marginTop: 8 }}
                                >
                                    <CpmButton
                                        variant="outline"
                                        size="modal"
                                        onClick={() => {
                                            if (onOpenSurvivor != null && survivorId != null) {
                                                onOpenSurvivor(level, survivorId);
                                                return;
                                            }
                                            onClose();
                                        }}
                                    >
                                        Open survivor in Master Editor
                                    </CpmButton>
                                    <CpmButton variant="navy" size="modal" onClick={onClose}>
                                        Back to {backLabel}
                                    </CpmButton>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
        </CpmModal>
    );
}
